import React, { useState, useCallback, useRef } from 'react';
import { Grid, Header, Message, Button, Icon } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { useSharedData } from '../../hooks/core/useZoneHooks';
import { useLitGroupOperationsZoneBased } from '../../hooks/entities/useLitGroupOperationsZoneBased';
import { LitGroupsListEnh } from '../lists';
import { LitGroupProfileForm } from '../forms';
import { DeleteLitGroupModal } from '../modals';

/**
 * LitGroupManagement-enh component
 * Enhanced version of LitGroupManagement with improved UI matching BookCreator components
 * Manages literary groups
 * @returns {JSX.Element} LitGroupManagement-enh component
 */
const LitGroupManagementEnh = () => {
  const sharedData = useSharedData();
  const { deleteLitGroup, getDeleteImpact } = useLitGroupOperationsZoneBased();

  // Ref for the form to call clear function
  const litGroupFormRef = useRef();

  // Extract data from new hook
  const {
    litGroups,
    books,
    selections,
    litGroupProfiles
  } = sharedData;

  // Map to old interface for compatibility
  const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const selectionsObj = selections || {};
  const litGroupId = selectionsObj.litGroupId;

  // Get the selected literary group profile
  const selectedLitGroupProfile = litGroupId ? litGroupProfiles[litGroupId] : null;

  // Get functions from shared data
  const fetchLitGroups = sharedData.actions.fetchLitGroups;
  const fetchBooks = sharedData.actions.fetchBooks;
  const setLitGroupSelection = sharedData.actions.setLitGroupSelection;
  const fetchLitGroupProfile = sharedData.actions.fetchLitGroupProfile;

  // State for editing mode
  const [isEditing, setIsEditing] = useState(!!litGroupId);

  // State for new group ID generation
  const [newGroupId, setNewGroupId] = useState(null);

  // State for delete confirmation modal
  const [deleteLitGroupModalOpen, setDeleteLitGroupModalOpen] = useState(false);
  const [litGroupToDelete, setLitGroupToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Handle literary group selection
  const handleLitGroupSelect = useCallback((group) => {
    if (group && group.uuid) {
      // Clear newGroupId when selecting existing group
      setNewGroupId(null);

      // Set the selection
      setLitGroupSelection(group.uuid);

      // Fetch the literary group profile
      fetchLitGroupProfile(group.uuid);

      // Fetch books for this literary group
      fetchBooks(group.uuid, true);

      setIsEditing(true);
    }
  }, [setLitGroupSelection, fetchLitGroupProfile, fetchBooks]);

  // Handle delete literary group
  const handleDeleteLitGroup = useCallback((litGroup) => {
    // Show the delete confirmation modal
    console.log("LitGroupManagement-enh handleDeleteLitGroup", litGroup)
    setLitGroupToDelete(litGroup);
    setDeleteLitGroupModalOpen(true);
  }, []);

  // Handler for confirming literary group deletion with delete type
  const confirmDeleteLitGroup = useCallback(async (deleteType) => {
    if (!litGroupToDelete || !litGroupToDelete.uuid) {
      console.error('No literary group to delete');
      return;
    }

    setDeleteLoading(true);

    try {
      // Call the delete function from the hook with delete type
      const result = await deleteLitGroup(litGroupToDelete.uuid, deleteType);

      // Show success message based on delete type
      const actionWord = deleteType === 'soft' ? 'deleted' :
        deleteType === 'archive' ? 'archived' :
          'permanently deleted';

      setSuccessMessage(`Literary group ${actionWord} successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Close the modal
      setDeleteLitGroupModalOpen(false);
      setLitGroupToDelete(null);
      setDeleteLoading(false);

      // If the deleted group was selected, clear the selection
      if (selectedLitGroupProfile && selectedLitGroupProfile.uuid === litGroupToDelete.uuid) {
        setLitGroupSelection(null);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error deleting literary group:', error);
      setErrorMessage('Failed to delete literary group: ' + (error.message || 'Unknown error'));
      setTimeout(() => setErrorMessage(''), 5000);
      setDeleteLoading(false);
    }
  }, [litGroupToDelete, selectedLitGroupProfile, setLitGroupSelection, deleteLitGroup]);

  // Handler for cancelling delete
  const cancelDeleteLitGroup = useCallback(() => {
    setDeleteLitGroupModalOpen(false);
    setLitGroupToDelete(null);
    setDeleteLoading(false);
  }, []);

  // Handle save
  const handleSaveProfile = useCallback((updatedProfile) => {
    // Refresh data after save
    if (updatedProfile && updatedProfile.uuid) {
      // Clear newGroupId since we now have a real UUID
      setNewGroupId(null);

      // Set the selection first - this already fetches LitGroup data and profile
      setLitGroupSelection(updatedProfile.uuid);

      // Only fetch books explicitly - don't re-fetch LitGroup data
      fetchBooks(updatedProfile.uuid, true);

      setIsEditing(true);

      // Show success message
      setSuccessMessage('Literary group saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setLitGroupSelection, fetchBooks]);

  // Handle new literary group
  const handleNewLitGroup = useCallback(() => {
    const newGroupId = crypto.randomUUID(); // Generate new UUID
    setNewGroupId(newGroupId); // Store the new group ID
    setLitGroupSelection(null); // Clear existing selection
    setIsEditing(true); // Set to true to show the form for creating new

    // Clear the profile image
    if (litGroupFormRef.current) {
      litGroupFormRef.current.clearProfileImage();
    }
  }, [setLitGroupSelection]);


  return (
    <ManagementHeader
      title="Literary Group Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6} >
            <LitGroupsListEnh
              onLitGroupSelect={handleLitGroupSelect}
              onDeleteLitGroup={handleDeleteLitGroup}
              onNewLiteraryGroup={handleNewLitGroup}
            />

            {/* <Button
              primary
              icon
              labelPosition="left"
              onClick={handleNewLitGroup}
              style={{ marginTop: '10px' }}
            >
              <Icon name="add" />
              New Literary Group
            </Button> */}
          </Grid.Column>

          <Grid.Column width={10}>
            {successMessage && (
              <Message positive>
                <Message.Header>Success</Message.Header>
                <p>{successMessage}</p>
              </Message>
            )}

            <Header as="h3" dividing>
              Literary Group Details
              {selectedLitGroupProfile && selectedLitGroupProfile.name && (
                <span className="selectedItemTitle"> - {selectedLitGroupProfile.name}</span>
              )}
            </Header>

            {!selectedLitGroupProfile && !isEditing ? (
              <div className="bw-empty-selection-message">
                <p>Select a literary group from the list to view or edit its details, or click "New Literary Group" to create one.</p>
              </div>
            ) : (
              <LitGroupProfileForm
                ref={litGroupFormRef}
                onSave={handleSaveProfile}
                selectedLitGroupProfile={selectedLitGroupProfile}
                litGroupId={litGroupId || newGroupId}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Enhanced Delete Literary Group Confirmation Modal */}
      <DeleteLitGroupModal
        open={deleteLitGroupModalOpen}
        litGroup={litGroupToDelete}
        onConfirm={confirmDeleteLitGroup}
        onCancel={cancelDeleteLitGroup}
        getDeleteImpact={getDeleteImpact}
        loading={deleteLoading}
        userRole="user" // TODO: Get actual user role from context/auth
      />
    </ManagementHeader>
  );
};

export default LitGroupManagementEnh;
